import React from 'react';
import {
  Typography,
  Box,
  Avatar,
  Link as MuiLink,
} from '@mui/material';
import {
  Circle as CircleIcon,
  Person as FollowIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

export default function FollowNotificationCard({ notification, onMarkRead }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const payload = notification.payload || {};
  
  const authorProfileUrl = payload.follower?.id ? `/profile/${payload.follower.id}` : null;
  // Following a lab notifies its PI: "<follower> started following your lab <lab>"
  const isLab = payload.content_type === 'lab' && payload.object_id;
  
  const handleMarkRead = () => {
    if (!notification.is_read && onMarkRead) {
      onMarkRead();
    }
  };
  
  
  const handleCardClick = () => {
    handleMarkRead();
    
        // Navigate to the specific item reflection page
        navigate(authorProfileUrl || '/profile');
  
    };   
  

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        transition: 'background-color 0.2s',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      {/* Avatar */}
      <Avatar
        src={payload.follower?.avatar}
        alt={payload.follower?.name || 'Follower'}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: theme.palette.primary.avatar,
          color: 'white'
        }}
      >
        {payload.follower?.name?.charAt(0) || <FollowIcon fontSize="small" />}
      </Avatar>

      {/* Single line text */}
      <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {authorProfileUrl ? (
          <MuiLink
            component="a"
            href={authorProfileUrl}
            underline="hover"
            color="text.primary"
            fontWeight={600}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'none' } }}
          >
            {payload.follower.name || 'Someone'}
          </MuiLink>
        ) : (
          <Box component="span" fontWeight={600}>
            {payload.follower.name || 'Someone'}
          </Box>
        )}
        {/* <FollowIcon sx={{ fontSize: 14, color: 'primary.main', mx: 0.25 }} /> */}
        {isLab ? (
          <>
            <Typography component="span" variant="body2" color="text.secondary">started following your lab</Typography>
            <MuiLink
              component="a"
              href={`/lab/${payload.object_id}/`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMarkRead(); navigate(`/lab/${payload.object_id}/`); }}
              underline="hover"
              color="text.primary"
              fontWeight={600}
            >
              {payload.entity_name || 'your lab'}
            </MuiLink>
          </>
        ) : (
          <Typography   component="span" variant="body2" color="text.secondary">
            connected with you
          </Typography>
        )}

      </Typography>

      {/* Timestamp and unread indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && (
          <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
        )}
      </Box>
    </Box>
  );
}
